import type { ApiResponse, PaginationData } from '@/types/api';

/** 任务业务类型：1-素材，2-文件夹 */
export const UPLOAD_TASK_TYPE = { MATERIAL: 1, FOLDER: 2 } as const;
export type UploadTaskType = (typeof UPLOAD_TASK_TYPE)[keyof typeof UPLOAD_TASK_TYPE];

/** 任务状态：0-处理中，1-已完成（成功 + 失败 = 总数时自动完成；重试会回退为处理中） */
export const UPLOAD_TASK_STATUS = { PROCESSING: 0, FINISHED: 1 } as const;
export type UploadTaskRecordStatus = (typeof UPLOAD_TASK_STATUS)[keyof typeof UPLOAD_TASK_STATUS];

/** 明细状态：0-待上传，1-上传成功，2-上传失败（回写接口只接受 1 / 2） */
export const UPLOAD_TASK_ITEM_STATUS = { PENDING: 0, SUCCESS: 1, FAILED: 2 } as const;
export type UploadTaskItemStatus =
  (typeof UPLOAD_TASK_ITEM_STATUS)[keyof typeof UPLOAD_TASK_ITEM_STATUS];

/** 回写接口允许的明细状态（禁止传 0-待上传） */
export type UploadTaskItemReportStatus =
  | typeof UPLOAD_TASK_ITEM_STATUS.SUCCESS
  | typeof UPLOAD_TASK_ITEM_STATUS.FAILED;

/** 创建任务：单个文件入参（不含路径，层级由 `folders` 树或 `folderId` 表达） */
export interface UploadTaskFileInput {
  /** 前端生成的唯一标识（nanoid），用于建立 `clientFileId ↔ itemId` 映射 */
  clientFileId: string;
  fileName: string;
}

/** 创建任务：目录树入参（空目录用 `files: []` 表达） */
export interface UploadTaskFolderInput {
  folderName: string;
  sortOrder: number;
  files: UploadTaskFileInput[];
  children: UploadTaskFolderInput[];
}

/** 创建任务入参 */
export interface CreateUploadTaskDto {
  taskType: UploadTaskType;
  /** 上传目标文件夹（文件夹类型下为挂载点） */
  folderId?: string;
  /** 设计师ID，与 creativeId 至少传一个 */
  designerId?: string;
  /** 创意人ID，与 designerId 至少传一个 */
  creativeId?: string;
  tagIds?: string[];
  /**
   * 所选文件夹分类：`personal` / `public` / `enterprise` / `department`（**后端必填**）。
   * 由前端根据上传目标文件夹反查（见 `resolveFolderCategory`），
   * 任务列表「跳转素材库」时与 `folderId` 一起用于精准选中。
   */
  folderCategory: string;
  /** 素材类型：扁平文件列表 */
  files?: UploadTaskFileInput[];
  /** 文件夹类型：目录树（`files` 可空表示空文件夹） */
  folders?: UploadTaskFolderInput[];
}

/** 创建任务响应：后端为每个明细分配的主键与落库目标 */
export interface UploadTaskItemBrief {
  itemId: string;
  clientFileId: string;
  /** 任务内目录节点 id（素材类型为 null） */
  taskFolderId?: string | null;
  /** 该明细的落库目标文件夹 id（文件夹类型是后端预建好的真实目录） */
  folderId: string;
  fileName: string;
}

export interface CreateUploadTaskResp {
  taskId: string;
  totalCount: number;
  /** 已落库的设定快照回显（调 `/material/save` 时原样携带，保证台账与落库一致） */
  designerId?: string;
  /** 已落库的创意人设定快照回显 */
  creativeId?: string;
  tagIds?: string[];
  folderCategory?: string;
  items: UploadTaskItemBrief[];
}

/** 任务头（列表行与详情头字段一致） */
export interface UploadTaskRecord {
  taskId: string;
  taskType: UploadTaskType;
  taskStatus: UploadTaskRecordStatus;
  /** 上传详情：总计 */
  totalCount: number;
  /** 上传详情：成功 */
  successCount: number;
  /** 上传详情：失败 */
  failCount: number;
  /** 上传目标文件夹 id（跳转素材库用） */
  folderId?: string;
  /** 上传文件夹全路径快照（目录被删/改名不影响展示） */
  folderPath?: string;
  /** 所选文件夹分类（跳转素材库时与 folderId 一起精准选中） */
  folderCategory?: string;
  designerId?: string;
  designerName?: string;
  /** 创意人ID（后端同步支持，台账展示预留） */
  creativeId?: string;
  /** 创意人名称 */
  creativeName?: string;
  tagIds?: string[];
  tagNames?: string[];
  /** 上传时间（任务创建时间） */
  createTime: string;
  /** 完成时间（重试回退为处理中时清空） */
  finishTime?: string | null;
}

/** 任务明细（素材类型 `items` 平铺；文件夹类型挂在 `folders` 树节点上） */
export interface UploadTaskItemRecord {
  itemId: string;
  clientFileId?: string;
  /** 任务内目录节点 id（素材类型为 null） */
  taskFolderId?: string | null;
  /** 该明细的落库目标文件夹 id */
  folderId: string;
  fileName: string;
  /** 未回写成功前为空 */
  ossKey?: string | null;
  fileMd5?: string | null;
  width?: number | null;
  height?: number | null;
  duration?: number | null;
  itemStatus: UploadTaskItemStatus;
  failReason?: string | null;
  materialId?: string | null;
  updateTime?: string | null;
}

/** 文件夹类型任务的目录树节点（**空文件夹节点保留**） */
export interface UploadTaskFolderRecord {
  taskFolderId: string;
  folderId: string;
  folderName: string;
  sortOrder: number;
  items: UploadTaskItemRecord[];
  children: UploadTaskFolderRecord[];
}

/**
 * 任务详情响应。
 *
 * `taskId / taskType / taskStatus / ...` 与 `items` / `folders` 同层，
 * 不存在 `{ task: {...} }` 这层包裹。明细二选一非空：
 * 素材类型给 `items` 平铺，文件夹类型给 `folders` 树。
 */
export type UploadTaskDetailResp = UploadTaskRecord & {
  items?: UploadTaskItemRecord[];
  folders?: UploadTaskFolderRecord[];
  /**
   * 兼容字段：个别实现会把头字段包一层 `task`。
   * 读取时统一走 `UploadTaskDetailDrawer` 里的容错取值（`detail.task ?? detail`），
   * 不要在新代码里直接依赖它。
   */
  task?: UploadTaskRecord & {
    items?: UploadTaskItemRecord[];
    folders?: UploadTaskFolderRecord[];
  };
};

/** 列表查询入参 */
export interface UploadTaskPageDto {
  pageNum: number;
  pageSize: number;
  object?: {
    /** 任务ID（后端精确匹配） */
    taskId?: string;
    taskStatus?: UploadTaskRecordStatus;
    /** 设计师筛选（多选） */
    designerIds?: string[];
    /** 上传时间（任务创建时间）范围 */
    uploadTimeBegin?: string;
    uploadTimeEnd?: string;
  };
}

/** 回写单条明细 */
export interface UpdateUploadTaskItemDto {
  itemId: string;
  /** 只接受 1-成功 / 2-失败 */
  itemStatus: UploadTaskItemReportStatus;
  /** `itemStatus = 1` 时必填 */
  ossKey?: string;
  /** 以下均为成功时的选填补录 */
  fileMd5?: string;
  width?: number;
  height?: number;
  duration?: number;
  materialId?: string;
  /** `itemStatus = 2` 时必填 */
  failReason?: string;
}

/** 批量回写明细状态（按完成批次上报，接口幂等，重复/迟到上报后端静默跳过） */
export interface UpdateUploadTaskStatusDto {
  taskId: string;
  items: UpdateUploadTaskItemDto[];
}

/** 回写响应：任务最新计数，用于刷新进度条 */
export interface UpdateUploadTaskStatusResp {
  taskId: string;
  taskStatus: UploadTaskRecordStatus;
  totalCount: number;
  successCount: number;
  failCount: number;
}

/** 重试失败明细入参 */
export interface RetryUploadTaskDto {
  taskId: string;
  itemIds: string[];
}

/** 重试响应：后端已把明细置回待上传、任务回退处理中 */
export interface RetryUploadTaskResp {
  taskId: string;
  taskStatus: UploadTaskRecordStatus;
  /** 任务创建时的设定快照，调 `/material/save` 时原样携带 */
  designerId?: string;
  /** 任务创建时的创意人设定快照，调 `/material/save` 时原样携带 */
  creativeId?: string;
  tagIds?: string[];
  folderCategory?: string;
  items: {
    itemId: string;
    clientFileId: string;
    folderId: string;
    fileName: string;
  }[];
}

/**
 * 上传任务接口签名。
 */
export interface UploadTaskApi {
  create(data: CreateUploadTaskDto): Promise<ApiResponse<CreateUploadTaskResp>>;
  page(data: UploadTaskPageDto): Promise<ApiResponse<PaginationData<UploadTaskRecord>>>;
  /** 详情：路径参数传 taskId */
  detail(taskId: string): Promise<ApiResponse<UploadTaskDetailResp>>;
  /** 回写明细状态（增量上报） */
  updateStatus(data: UpdateUploadTaskStatusDto): Promise<ApiResponse<UpdateUploadTaskStatusResp>>;
  /** 重试失败明细：后端把明细置回待上传并回传落库目标 */
  retry(data: RetryUploadTaskDto): Promise<ApiResponse<RetryUploadTaskResp>>;
}
